// ===================================================
// useNotificationRouting.js — 알림 탭 시 화면 라우팅 훅
//
// 역할:
//   사용자가 로컬 알림을 탭했을 때, 알림 유형에 맞는 화면으로
//   자동 이동시켜줍니다.
//
//   예) "기억 되살리기" 알림 탭 → 채팅 화면으로 이동
//       "스트릭 축하" 알림 탭 → 캘린더 화면으로 이동
//
//   Capacitor LocalNotifications의 'localNotificationActionPerformed'
//   이벤트를 수신하며, 알림의 extra.type 값으로 라우팅합니다.
//
//   웹 환경에서는 아무 동작 없이 무시됩니다.
// ===================================================

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

// 알림 타입 → 이동할 화면 매핑
// 여기에 없는 타입(daily-reminder, streak-danger, re-engagement, first-record)은
// 기본 화면(landing)에 머무릅니다.
const ROUTE_MAP = {
  'memory-resurfacing': 'chat',     // B. 기억 되살리기 → 채팅
  'weekly-review':      'chat',     // C. 주간 회고 → 채팅
  'decision-followup':  'chat',     // F. 고민 후속 → 채팅
  'dream-firststep':    'chat',     // G. 꿈 첫 걸음 → 채팅
  'unread-chat':        'chat',     // I. 미확인 채팅 → 채팅
  'streak-celebrate':   'calendar', // D-1. 스트릭 축하 → 캘린더
};

/**
 * 알림 탭 라우팅 훅
 * @param {Object} params
 * @param {Function} params.setScreen - 화면 전환 함수 (useSessionManager에서 제공)
 * @param {Object|null} params.user - 현재 로그인한 사용자 (null이면 비로그인)
 */
export function useNotificationRouting({ setScreen, user }) {
  useEffect(() => {
    // 웹 환경에서는 알림이 없으므로 무시
    if (!Capacitor.isNativePlatform()) return;

    let listener;

    const setup = async () => {
      // 사용자가 알림을 탭했을 때 발생하는 이벤트 수신
      // 앱이 종료 상태였다면 Capacitor가 이벤트를 큐잉해뒀다가
      // 리스너 등록 시점에 전달합니다.
      listener = await LocalNotifications.addListener(
        'localNotificationActionPerformed',
        (event) => {
          const type = event.notification?.extra?.type;
          if (!type) return; // extra가 없는 구버전 알림은 무시

          const target = ROUTE_MAP[type];
          if (target) {
            // 채팅 화면은 로그인 필요 — 비로그인 시 landing에 머무름
            if (target === 'chat' && !user) return;
            setScreen(target);
          }
        }
      );
    };

    setup();

    // 컴포넌트 언마운트 시 리스너 정리
    return () => {
      if (listener) listener.remove();
    };
  }, [setScreen, user]);
}
